// ============================================================================
// RADAR DATA HOOK — useRadar.js
// ============================================================================
// Computes radar items from existing app data. Each rule family produces
// normalized RadarItem objects. The hook merges items with persisted statuses
// from useRadarSettings and applies time-bucket classification.
//
// Architecture: Frontend aggregation over data already loaded by App.js.
// Rule functions are modular and independently testable.
// ============================================================================

import { useMemo } from 'react';
import { calculateDueDate, getInvoiceDisplayStatus } from '../utils/emailTemplates';
import { RULE_META } from './useRadarSettings';

// ── Helpers ──────────────────────────────────────────────────────────────────

const today = () => new Date().toISOString().slice(0, 10);
const daysBetween = (a, b) => {
  if (!a || !b) return null;
  return Math.round((new Date(b) - new Date(a)) / 86400000);
};

// Count calendar days in [fromDate, toDate] (inclusive on both ends) that fall
// inside any of the user's absence ranges. Used by ruleTimeNotTracked to
// discount leave days from a tracking gap so we don't warn while someone is
// vacationing / sick / on personal leave.
const ABSENCE_TYPES = new Set(['vacation', 'sick', 'personal']);
function buildUserLeaveRanges(timeOff) {
  const map = new Map();
  for (const r of timeOff || []) {
    if (!r || !r.startDate || !r.endDate) continue;
    if (r.status === 'rejected' || r.status === 'cancelled') continue;
    if (!ABSENCE_TYPES.has(r.type)) continue;
    const uid = String(r.userId);
    if (!map.has(uid)) map.set(uid, []);
    map.get(uid).push({ start: r.startDate, end: r.endDate });
  }
  return map;
}
function countLeaveDaysInRange(ranges, fromDate, toDate) {
  if (!ranges || ranges.length === 0) return 0;
  if (!fromDate || !toDate || fromDate > toDate) return 0;
  let count = 0;
  const cursor = new Date(fromDate);
  const end = new Date(toDate);
  while (cursor <= end) {
    const dateStr = cursor.toISOString().slice(0, 10);
    for (const r of ranges) {
      if (dateStr >= r.start && dateStr <= r.end) { count++; break; }
    }
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return count;
}
function addDaysISO(dateStr, n) {
  const d = new Date(dateStr);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// Classify items into time buckets based on due_date / detected_at / days_overdue
function computeTimeBucket(item) {
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);
  const endOfWeek = new Date(now);
  endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));
  const endOfWeekStr = endOfWeek.toISOString().slice(0, 10);

  // Overdue items: today if severely overdue or critical, this_week otherwise
  if (item.days_overdue && item.days_overdue > 0) {
    if (item.severity === 'critical' || item.days_overdue > 14) return 'today';
    if (item.days_overdue > 0) return 'this_week';
  }

  // Items with due dates
  if (item.due_date) {
    if (item.due_date <= todayStr) return 'today';
    if (item.due_date <= endOfWeekStr) return 'this_week';
    return 'upcoming';
  }

  // Signals: use detected_at for recency
  if (item.severity === 'signal' && item.detected_at) {
    if (item.detected_at >= todayStr) return 'today';
    const daysAgo = daysBetween(item.detected_at, todayStr);
    if (daysAgo <= 7) return 'this_week';
    return 'upcoming';
  }

  // Default: items without dates go to this_week
  return 'this_week';
}

// Group radar items into time buckets (today / this_week / upcoming) by domain.
//
// An alert's identity is (type, entity_type, entity_id) — the same key used to
// match persisted statuses and to build item.id. Normally each identity maps to
// a single bucket, but a rule can legitimately emit the same identity more than
// once (e.g. a project with two overdue timeline stages shares one alert id). To
// avoid showing the exact same alert in more than one bucket — or twice within a
// bucket — we keep only its most-urgent occurrence: earliest bucket first, then
// higher severity, then more days overdue. Distinct alerts (different ids, e.g.
// two different invoices for the same project) are always preserved.
//
// Exported for unit testing.
export function groupItemsByBucket(items) {
  const buckets = { today: {}, this_week: {}, upcoming: {} };
  const domains = ['finance', 'projects', 'bd', 'hiring', 'team', 'system'];

  for (const bucket of Object.keys(buckets)) {
    for (const domain of domains) {
      buckets[bucket][domain] = [];
    }
  }

  const BUCKET_ORDER = { today: 0, this_week: 1, upcoming: 2 };
  const SEV_ORDER = { critical: 0, warning: 1, signal: 2 };

  // Collapse repeated alert identities to their single most-urgent occurrence.
  const representativeById = new Map();
  for (const item of items) {
    if (!(item.time_bucket in BUCKET_ORDER)) continue;
    const existing = representativeById.get(item.id);
    if (!existing) {
      representativeById.set(item.id, item);
      continue;
    }
    const moreUrgent =
      (BUCKET_ORDER[item.time_bucket] - BUCKET_ORDER[existing.time_bucket]) ||
      ((SEV_ORDER[item.severity] ?? 3) - (SEV_ORDER[existing.severity] ?? 3)) ||
      ((existing.days_overdue || 0) - (item.days_overdue || 0));
    if (moreUrgent < 0) representativeById.set(item.id, item);
  }

  for (const item of representativeById.values()) {
    const bucket = buckets[item.time_bucket];
    if (bucket && bucket[item.domain]) {
      bucket[item.domain].push(item);
    }
  }

  return buckets;
}

// Create a standardized radar item
function makeItem(overrides) {
  return {
    id: overrides.id || `${overrides.type}-${overrides.entity_type}-${overrides.entity_id}`,
    type: overrides.type,
    title: overrides.title || '',
    description: overrides.description || '',
    severity: overrides.severity || 'warning',
    time_bucket: null, // computed after creation
    domain: overrides.domain || RULE_META[overrides.type]?.domain || 'system',
    source_module: overrides.source_module || overrides.type,
    entity_type: overrides.entity_type || '',
    entity_id: overrides.entity_id || '',
    entity_label: overrides.entity_label || '',
    project_id: overrides.project_id || null,
    client_id: overrides.client_id || null,
    user_id: overrides.user_id || null,
    detected_at: overrides.detected_at || today(),
    due_date: overrides.due_date || null,
    days_overdue: overrides.days_overdue || null,
    financial_impact: overrides.financial_impact || null,
    recommended_action: overrides.recommended_action || '',
    navigation_target: overrides.navigation_target || null,
    navigation_params: overrides.navigation_params || null,
    is_silent_failure: overrides.is_silent_failure || false,
    meta: overrides.meta || null,
    status: 'open',
    acknowledged_at: null,
    snoozed_until: null,
    resolved_at: null,
  };
}

// ── Rule Functions ───────────────────────────────────────────────────────────
// Each returns an array of radar items.

// FINANCE: Invoice overdue / not paid on time
function ruleInvoiceOverdue(invoices, clients, projects, config) {
  const items = [];
  const todayStr = today();
  for (const inv of invoices) {
    if (inv.status === 'paid' || inv.paidConfirmed) continue;
    if (inv.status !== 'issued') continue;

    const client = clients.find(c => String(c.id) === String(inv.clientId));
    const project = projects.find(p => String(p.id) === String(inv.projectId));
    const { dueDate } = calculateDueDate(inv.invoiceDate, client?.paymentTermsDays);
    const displayStatus = getInvoiceDisplayStatus(inv, dueDate);
    if (displayStatus !== 'overdue') continue;

    const daysOver = daysBetween(dueDate, todayStr);
    const severity = daysOver >= (config.criticalDays || 30) ? 'critical' : 'warning';

    items.push(makeItem({
      type: 'invoice_overdue',
      title: `Invoice ${inv.invoiceNumber || '#' + inv.id} overdue`,
      description: `${project?.name || 'Unknown project'} — ${daysOver} days past due date (${dueDate})`,
      severity,
      entity_type: 'invoice',
      entity_id: inv.id,
      entity_label: inv.invoiceNumber || `Invoice #${inv.id}`,
      project_id: inv.projectId,
      client_id: inv.clientId,
      due_date: dueDate,
      days_overdue: daysOver,
      financial_impact: inv.grossAmount || inv.netAmount || inv.baseAmount || null,
      recommended_action: 'Send payment reminder to client',
      navigation_target: 'invoices',
      navigation_params: { projectId: inv.projectId },
    }));
  }
  return items;
}

// FINANCE: Invoice generated (draft) but not sent
function ruleInvoiceNotSent(invoices, projects, config) {
  const items = [];
  const todayStr = today();
  for (const inv of invoices) {
    if (inv.status !== 'draft') continue;
    // Check how old the draft is
    const daysOld = daysBetween(inv.invoiceDate || inv.createdAt, todayStr);
    if (daysOld < (config.daysAfterStageClose || 7)) continue;

    const project = projects.find(p => String(p.id) === String(inv.projectId));
    items.push(makeItem({
      type: 'invoice_not_sent',
      title: `Draft invoice not sent`,
      description: `${project?.name || 'Unknown project'} — draft created ${daysOld} days ago`,
      severity: 'warning',
      entity_type: 'invoice',
      entity_id: inv.id,
      entity_label: inv.invoiceNumber || `Invoice #${inv.id}`,
      project_id: inv.projectId,
      client_id: inv.clientId,
      days_overdue: daysOld,
      is_silent_failure: true,
      recommended_action: 'Review and issue this invoice',
      navigation_target: 'invoices',
      navigation_params: { projectId: inv.projectId },
    }));
  }
  return items;
}

// FINANCE: Payment received (signal)
function rulePaymentReceived(invoices) {
  const items = [];
  const todayStr = today();
  const recentWindow = 7;
  for (const inv of invoices) {
    if (!inv.paidConfirmed || !inv.paidDate) continue;
    const daysAgo = daysBetween(inv.paidDate, todayStr);
    if (daysAgo > recentWindow) continue;

    items.push(makeItem({
      type: 'payment_received',
      title: `Payment received`,
      description: `${inv.invoiceNumber || 'Invoice #' + inv.id} — paid on ${inv.paidDate}`,
      severity: 'signal',
      entity_type: 'invoice',
      entity_id: inv.id,
      entity_label: inv.invoiceNumber || `Invoice #${inv.id}`,
      project_id: inv.projectId,
      client_id: inv.clientId,
      detected_at: inv.paidDate,
      financial_impact: inv.grossAmount || inv.netAmount || inv.baseAmount || null,
      recommended_action: 'Confirm payment details',
      navigation_target: 'invoices',
      navigation_params: { projectId: inv.projectId },
    }));
  }
  return items;
}

// FINANCE: Upcoming invoice reminders (approaching due)
function ruleInvoiceReminder(invoices, clients, projects, config) {
  const items = [];
  const todayStr = today();
  const daysBeforeDue = config.daysBeforeDue || 7;

  for (const inv of invoices) {
    if (inv.status !== 'issued' || inv.paidConfirmed) continue;
    const client = clients.find(c => String(c.id) === String(inv.clientId));
    const { dueDate } = calculateDueDate(inv.invoiceDate, client?.paymentTermsDays);
    if (!dueDate) continue;

    const daysUntilDue = daysBetween(todayStr, dueDate);
    // Only show if due soon but not yet overdue
    if (daysUntilDue < 0 || daysUntilDue > daysBeforeDue) continue;

    const project = projects.find(p => String(p.id) === String(inv.projectId));
    items.push(makeItem({
      type: 'invoice_reminder',
      title: `Invoice due ${daysUntilDue === 0 ? 'today' : `in ${daysUntilDue} days`}`,
      description: `${project?.name || 'Unknown project'} — ${inv.invoiceNumber || '#' + inv.id}`,
      severity: daysUntilDue <= 2 ? 'warning' : 'signal',
      entity_type: 'invoice',
      entity_id: inv.id,
      entity_label: inv.invoiceNumber || `Invoice #${inv.id}`,
      project_id: inv.projectId,
      client_id: inv.clientId,
      due_date: dueDate,
      financial_impact: inv.grossAmount || inv.netAmount || inv.baseAmount || null,
      recommended_action: 'Follow up on payment before due date',
      navigation_target: 'invoices',
      navigation_params: { projectId: inv.projectId },
    }));
  }
  return items;
}

// FINANCE: CA monthly reminder — company-wide, persists until all invoices archived + sent
function ruleCaMonthlyReminder(config, invoices) {
  const items = [];
  const now = new Date();
  const dayOfMonth = now.getDate();
  const threshold = config.dayOfMonth || 10;

  // Previous month info
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevMonthKey = `${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, '0')}`;
  const monthLabel = prevMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Tax invoices for the previous month (issued or paid)
  const taxInvoices = (invoices || []).filter(inv => {
    const dt = inv.invoiceDate || '';
    const month = dt.slice(0, 7);
    const docType = inv.invoiceDocType || 'tax';
    const status = inv.status || '';
    return month === prevMonthKey
      && docType === 'tax'
      && (status === 'issued' || status === 'paid');
  });

  const allArchived = taxInvoices.length > 0 && taxInvoices.every(i => i.archivedToStorage);
  const allSent = taxInvoices.length > 0 && taxInvoices.every(i => i.sentToAccountant);
  const isComplete = allArchived && allSent;

  // Show until completed (not just first N days)
  if (!isComplete) {
    const archivedCount = taxInvoices.filter(i => i.archivedToStorage).length;
    const sentCount = taxInvoices.filter(i => i.sentToAccountant).length;

    let description;
    if (taxInvoices.length === 0) {
      description = `No tax invoices found for ${monthLabel}. Mark as complete if nothing to send.`;
    } else {
      const parts = [];
      parts.push(`${taxInvoices.length} tax invoice${taxInvoices.length !== 1 ? 's' : ''}`);
      if (archivedCount < taxInvoices.length) parts.push(`${taxInvoices.length - archivedCount} not yet archived`);
      if (sentCount < taxInvoices.length) parts.push(`${taxInvoices.length - sentCount} not sent to CA`);
      description = parts.join(' — ');
    }

    const severity = dayOfMonth > threshold ? 'critical'
      : dayOfMonth >= (threshold - 2) ? 'warning'
      : 'signal';

    items.push(makeItem({
      type: 'ca_monthly_reminder',
      title: `Send ${monthLabel} invoices to CA`,
      description,
      severity,
      domain: 'finance',
      entity_type: 'system',
      entity_id: `ca-${prevMonthKey}`,
      entity_label: 'Monthly Archive & CA Workflow',
      days_overdue: dayOfMonth, // always show in "today" bucket
      is_silent_failure: dayOfMonth > threshold,
      recommended_action: 'Archive invoices and send to Chartered Accountant',
      navigation_target: null,
      // Store invoice data for the Radar panel
      meta: {
        prevMonthKey,
        monthLabel,
        taxInvoiceCount: taxInvoices.length,
        archivedCount,
        sentCount,
      },
    }));
  }
  return items;
}

// PROJECTS: Timeline milestone overdue
function ruleTimelineOverdue(projects, config) {
  const items = [];
  const todayStr = today();
  const STAGE_NAMES = {
    SD: 'Schematic Design', DD: 'Design Development', CD: 'Construction Docs',
    T: 'Tender', CA: 'Construction Admin', PC: 'Project Closeout',
  };

  for (const project of projects) {
    if (project.status === 'completed' || project.status === 'archived') continue;
    const timeline = project.stageTimeline;
    if (!timeline || typeof timeline !== 'object') continue;

    for (const [stageKey, stageData] of Object.entries(timeline)) {
      // Check if planned end date is overdue and stage not completed
      const plannedEnd = stageData.plannedEnd || stageData.endDate;
      if (!plannedEnd) continue;
      if (stageData.actualDate) continue; // stage already completed

      const daysOver = daysBetween(plannedEnd, todayStr);
      if (daysOver <= 0) continue;

      const severity = daysOver >= (config.criticalDays || 30) ? 'critical' : 'warning';
      items.push(makeItem({
        type: 'timeline_overdue',
        title: `${STAGE_NAMES[stageKey] || stageKey} overdue`,
        description: `${project.name} — ${daysOver} days past planned date (${plannedEnd})`,
        severity,
        entity_type: 'project',
        entity_id: project.id,
        entity_label: project.name,
        project_id: project.id,
        client_id: project.clientId,
        due_date: plannedEnd,
        days_overdue: daysOver,
        recommended_action: `Review timeline for ${STAGE_NAMES[stageKey] || stageKey}`,
        navigation_target: 'timeline',
        navigation_params: { projectId: project.id },
      }));
    }
  }
  return items;
}

// PROJECTS: Profitability at risk (cumulative income < cumulative expenses)
function ruleProfitabilityRisk(projects, invoices, timeEntries, users, fmtCurr) {
  const items = [];

  for (const project of projects) {
    if (project.status === 'completed' || project.status === 'archived') continue;
    if (!project.budget || project.budget <= 0) continue;

    // Calculate cumulative income: sum of paid invoice amounts
    const projectInvoices = invoices.filter(inv =>
      String(inv.projectId) === String(project.id) && (inv.status === 'paid' || inv.paidConfirmed)
    );
    const cumulativeIncome = projectInvoices.reduce((sum, inv) =>
      sum + (inv.grossAmount || inv.netAmount || inv.baseAmount || 0), 0);

    // Calculate cumulative expenses: salary cost based on hours logged
    const projectEntries = timeEntries.filter(e => String(e.projectId) === String(project.id));
    const totalHours = projectEntries.reduce((sum, e) => sum + ((e.duration || 0) / 3600), 0);

    // Simple cost estimate: average hourly cost rate × hours
    let avgHourlyCost = 0;
    if (users.length > 0) {
      // costRate is monthly salary; divide by 176 to get hourly rate
      const totalHourlyCost = users.reduce((sum, u) => sum + ((u.costRate || 0) / 176), 0);
      avgHourlyCost = totalHourlyCost / users.length;
    }
    const estimatedExpenses = totalHours * avgHourlyCost;

    if (cumulativeIncome > 0 && estimatedExpenses > cumulativeIncome) {
      const deficit = estimatedExpenses - cumulativeIncome;
      items.push(makeItem({
        type: 'profitability_risk',
        title: `Profitability at risk`,
        description: `${project.name} — expenses exceed income by ${fmtCurr ? fmtCurr(deficit) : Math.round(deficit)}`,
        severity: 'critical',
        entity_type: 'project',
        entity_id: project.id,
        entity_label: project.name,
        project_id: project.id,
        client_id: project.clientId,
        financial_impact: deficit,
        is_silent_failure: true,
        recommended_action: 'Review project costs and invoicing schedule',
        navigation_target: 'manage-projects',
        navigation_params: { projectId: project.id },
      }));
    }
  }
  return items;
}

// BD: New inquiry received (signal)
function ruleNewInquiry(inquiries, config) {
  const items = [];
  const todayStr = today();
  const recentDays = config.recentDays || 7;

  for (const inq of inquiries) {
    // Only surface signals for active inquiries — parked (not_converted),
    // converted, and archived inquiries aren't new work to act on.
    if (inq.status !== 'active') continue;
    const daysAgo = daysBetween(inq.inquiryDate, todayStr);
    if (daysAgo > recentDays) continue;

    items.push(makeItem({
      type: 'new_inquiry',
      title: `New inquiry received`,
      description: `${inq.projectName || inq.clientName || 'Unknown'} — ${inq.inquiryDate}`,
      severity: 'signal',
      entity_type: 'inquiry',
      entity_id: inq.id,
      entity_label: inq.projectName || `Inquiry #${inq.id}`,
      client_id: inq.clientId,
      detected_at: inq.inquiryDate,
      recommended_action: 'Review inquiry and plan follow-up',
      navigation_target: 'inquiries',
      navigation_params: { inquiryId: inq.id },
    }));
  }
  return items;
}

// BD: Inquiry follow-up overdue
function ruleInquiryFollowupOverdue(inquiries, config) {
  const items = [];
  const todayStr = today();

  for (const inq of inquiries) {
    // Only active inquiries need follow-up nudges. not_converted inquiries are
    // deliberately parked (paused / on hold / to be revisited) and shouldn't
    // surface as overdue follow-ups; converted/archived are closed.
    if (inq.status !== 'active') continue;

    // Check explicit next follow-up date
    if (inq.nextFollowupDate && inq.nextFollowupDate < todayStr) {
      const daysOver = daysBetween(inq.nextFollowupDate, todayStr);
      const severity = daysOver >= (config.criticalDays || 14) ? 'critical' : 'warning';

      items.push(makeItem({
        type: 'inquiry_followup_overdue',
        title: `Inquiry follow-up overdue`,
        description: `${inq.projectName || 'Unknown'} — ${daysOver} days past follow-up date`,
        severity,
        entity_type: 'inquiry',
        entity_id: inq.id,
        entity_label: inq.projectName || `Inquiry #${inq.id}`,
        client_id: inq.clientId,
        due_date: inq.nextFollowupDate,
        days_overdue: daysOver,
        is_silent_failure: true,
        recommended_action: 'Contact the client or update follow-up status',
        navigation_target: 'inquiries',
        navigation_params: { inquiryId: inq.id },
      }));
    }
    // If no explicit follow-up date, check if last follow-up was long ago
    else if (!inq.nextFollowupDate && inq.lastFollowupDate) {
      const daysSince = daysBetween(inq.lastFollowupDate, todayStr);
      if (daysSince > 30) {
        items.push(makeItem({
          type: 'inquiry_followup_overdue',
          title: `Inquiry needs follow-up`,
          description: `${inq.projectName || 'Unknown'} — ${daysSince} days since last contact`,
          severity: 'warning',
          entity_type: 'inquiry',
          entity_id: `${inq.id}-stale`,
          entity_label: inq.projectName || `Inquiry #${inq.id}`,
          client_id: inq.clientId,
          days_overdue: daysSince - 30,
          is_silent_failure: true,
          recommended_action: 'Schedule a follow-up call or meeting',
          navigation_target: 'inquiries',
          navigation_params: { inquiryId: inq.id },
        }));
      }
    }
  }
  return items;
}

// HIRING: Candidate stagnation
function ruleHiringStagnation(candidates, interviews, config) {
  const items = [];
  const todayStr = today();
  const daysFirst = config.daysInFirstStage || 15;
  const daysPost = config.daysPostInterview || 15;

  for (const cand of candidates) {
    if (cand.isArchived) continue;

    // Check if candidate has been in the pipeline without interview for too long
    const candidateInterviews = interviews.filter(i =>
      String(i.candidateId) === String(cand.id)
    );

    if (candidateInterviews.length === 0) {
      // No interviews scheduled — check how long candidate has been in first stage
      const daysInPipeline = daysBetween(cand.createdAt, todayStr);
      if (daysInPipeline >= daysFirst) {
        items.push(makeItem({
          type: 'hiring_stagnation',
          title: `Candidate needs attention`,
          description: `${cand.candidateName} — ${daysInPipeline} days without interview`,
          severity: daysInPipeline > daysFirst * 2 ? 'critical' : 'warning',
          entity_type: 'hiring_candidate',
          entity_id: cand.id,
          entity_label: cand.candidateName,
          days_overdue: daysInPipeline - daysFirst,
          recommended_action: 'Schedule interview or archive candidate',
          navigation_target: 'hiring',
          navigation_params: { candidateId: cand.id },
        }));
      }
    } else {
      // Has interviews — check if decision is pending after last interview
      const latestInterview = candidateInterviews.sort((a, b) =>
        (b.interviewDate || '').localeCompare(a.interviewDate || '')
      )[0];

      if (latestInterview?.interviewDate) {
        const daysSinceInterview = daysBetween(latestInterview.interviewDate, todayStr);
        if (daysSinceInterview >= daysPost) {
          items.push(makeItem({
            type: 'hiring_stagnation',
            title: `Post-interview decision needed`,
            description: `${cand.candidateName} — ${daysSinceInterview} days since last interview`,
            severity: daysSinceInterview > daysPost * 2 ? 'critical' : 'warning',
            entity_type: 'hiring_candidate',
            entity_id: cand.id,
            entity_label: cand.candidateName,
            days_overdue: daysSinceInterview - daysPost,
            is_silent_failure: true,
            recommended_action: 'Hire, reject, or move to future potential',
            navigation_target: 'hiring',
            navigation_params: { candidateId: cand.id },
          }));
        }
      }
    }
  }
  return items;
}

// HIRING: Pending admin actions (offer/hire email requests from supervisors)
function ruleHiringAdminAction(candidates) {
  const items = [];
  for (const cand of candidates) {
    if (!cand.pendingAdminAction) continue;
    // Skip archived candidates UNLESS they have a post-hire letter action
    if (cand.isArchived && cand.pendingAdminAction !== 'send_hire_letter') continue;
    const actionLabel = cand.pendingAdminAction === 'offer'
      ? 'Send offer email'
      : cand.pendingAdminAction === 'send_hire_letter'
        ? 'Send hire confirmation letter'
        : 'Hire candidate';
    const actionDesc = cand.pendingAdminAction === 'offer'
      ? `Offer position email requested for ${cand.candidateName}`
      : cand.pendingAdminAction === 'send_hire_letter'
        ? `Send confirmation letter for newly hired ${cand.candidateName}`
        : `Hire action requested for ${cand.candidateName}`;
    const requestedBy = cand.pendingAdminActionBy || '';
    const daysPending = cand.pendingAdminActionAt
      ? daysBetween(cand.pendingAdminActionAt.slice(0, 10), today())
      : 0;

    items.push(makeItem({
      type: 'hiring_admin_action',
      title: actionLabel,
      description: actionDesc + (cand.jobRole ? ` — ${cand.jobRole}` : ''),
      severity: daysPending > 2 ? 'critical' : 'warning',
      domain: 'hiring',
      entity_type: 'hiring_candidate',
      entity_id: `${cand.id}-${cand.pendingAdminAction}`,
      entity_label: cand.candidateName,
      days_overdue: daysPending,
      detected_at: cand.pendingAdminActionAt || cand.updatedAt || cand.createdAt,
      recommended_action: actionLabel,
      navigation_target: 'hiring',
      navigation_params: {
        candidateId: cand.id,
        pendingAction: cand.pendingAdminAction,
      },
      meta: {
        candidateId: cand.id,
        candidateName: cand.candidateName,
        jobRole: cand.jobRole || '',
        actionType: cand.pendingAdminAction,
        requestedBy,
      },
    }));
  }
  return items;
}

// BD: Client birthdays coming up
function ruleClientBirthday(clients, config) {
  const items = [];
  const todayStr = today();
  const daysAhead = config.daysAhead || 7;
  const nowMs = new Date(todayStr).getTime();

  for (const client of clients) {
    if (!client.dob) continue;
    const parts = client.dob.split('-');
    if (parts.length !== 3) continue;

    const currentYear = new Date().getFullYear();
    const birthdayThisYear = `${currentYear}-${parts[1]}-${parts[2]}`;
    const birthdayMs = new Date(birthdayThisYear).getTime();
    const daysUntil = Math.round((birthdayMs - nowMs) / 86400000);

    if (daysUntil >= -3 && daysUntil <= daysAhead) {
      const name = client.organisation || client.name || 'Client';
      items.push(makeItem({
        type: 'client_birthday',
        title: daysUntil === 0 ? `${name} — Birthday today!` : daysUntil > 0 ? `${name} — Birthday in ${daysUntil} days` : `${name} — Birthday was ${Math.abs(daysUntil)} day${Math.abs(daysUntil) !== 1 ? 's' : ''} ago`,
        description: `${name}'s birthday is on ${birthdayThisYear}. Send wishes!`,
        severity: daysUntil === 0 ? 'critical' : 'warning',
        domain: 'bd',
        entity_type: 'client',
        entity_id: client.id,
        entity_label: name,
        client_id: client.id,
        days_overdue: 0,
        due_date: birthdayThisYear,
        recommended_action: 'Send birthday wishes email',
        navigation_target: 'clients',
        navigation_params: { clientId: client.id },
        meta: { clientName: name, eventDate: birthdayThisYear, eventType: 'birthday' },
      }));
    }
  }
  return items;
}

// BD: Client anniversaries coming up
function ruleClientAnniversary(clients, config) {
  const items = [];
  const todayStr = today();
  const daysAhead = config.daysAhead || 7;
  const nowMs = new Date(todayStr).getTime();

  for (const client of clients) {
    if (!client.anniversary) continue;
    const parts = client.anniversary.split('-');
    if (parts.length !== 3) continue;

    const currentYear = new Date().getFullYear();
    const annivThisYear = `${currentYear}-${parts[1]}-${parts[2]}`;
    const annivMs = new Date(annivThisYear).getTime();
    const daysUntil = Math.round((annivMs - nowMs) / 86400000);

    if (daysUntil >= -3 && daysUntil <= daysAhead) {
      const name = client.organisation || client.name || 'Client';
      const years = currentYear - parseInt(parts[0], 10);
      items.push(makeItem({
        type: 'client_anniversary',
        title: daysUntil === 0 ? `${name} — Anniversary today!` : daysUntil > 0 ? `${name} — Anniversary in ${daysUntil} days` : `${name} — Anniversary was ${Math.abs(daysUntil)} day${Math.abs(daysUntil) !== 1 ? 's' : ''} ago`,
        description: `${name}'s ${years > 0 ? years + ' year ' : ''}anniversary is on ${annivThisYear}. Send wishes!`,
        severity: daysUntil === 0 ? 'critical' : 'warning',
        domain: 'bd',
        entity_type: 'client',
        entity_id: client.id,
        entity_label: name,
        client_id: client.id,
        days_overdue: 0,
        due_date: annivThisYear,
        recommended_action: 'Send anniversary wishes email',
        navigation_target: 'clients',
        navigation_params: { clientId: client.id },
        meta: { clientName: name, eventDate: annivThisYear, eventType: 'anniversary', years },
      }));
    }
  }
  return items;
}

// TEAM: Users not tracking hours
// Returns true if a user is unassigned or floating in the current week's HFB board.
// weeklyAssignments: array of { userId, teamId } from weekly_team_assignments for the current week.
// When weeklyAssignments is null/empty the check is skipped (returns false).
function isUnassignedOrFloating(user, weeklyAssignments) {
  if (!weeklyAssignments || weeklyAssignments.length === 0) return false;
  const assignment = weeklyAssignments.find(a => String(a.userId) === String(user.id));
  if (!assignment) return true; // no row = unassigned or floating-by-role
  return assignment.teamId === null || assignment.teamId === undefined; // explicit floating
}

// userLastEntryDates: { [userId]: 'YYYY-MM-DD' } — DB-sourced, covers ALL entries,
// not just the paginated slice loaded into the frontend.
// weeklyAssignments: current week's rows from weekly_team_assignments (optional).
// timeOff: rows from time_off_requests; approved/pending absence days are
// subtracted from the gap so we don't warn while someone is on leave.
function ruleTimeNotTracked(users, config, userLastEntryDates, weeklyAssignments, timeOff) {
  const items = [];
  const todayStr = today();
  const maxDays = config.maxUntrackedDays || 5;
  const hasDbMap = userLastEntryDates && Object.keys(userLastEntryDates).length > 0;

  // Only check active users (isActive !== false covers null/undefined legacy default;
  // status !== 'inactive' covers explicit deactivation via lifecycle status)
  const activeUsers = users.filter(u => u.isActive !== false && u.status !== 'inactive' && u.role !== 'admin');

  // Without the DB-sourced map we can't tell "never tracked" from "tracked outside
  // the paginated slice", so skip this rule entirely rather than emit false warnings.
  if (!hasDbMap) return items;

  const leaveRangesByUser = buildUserLeaveRanges(timeOff);

  for (const user of activeUsers) {
    if (isUnassignedOrFloating(user, weeklyAssignments)) continue;

    const latestDate = userLastEntryDates[user.id] || userLastEntryDates[String(user.id)] || null;
    const userLeave = leaveRangesByUser.get(String(user.id)) || [];

    if (!latestDate) {
      // No entries anywhere — check join date
      const daysSinceJoin = daysBetween(user.joinDate, todayStr);
      const leaveDays = user.joinDate
        ? countLeaveDaysInRange(userLeave, addDaysISO(user.joinDate, 1), todayStr)
        : 0;
      const adjustedDays = (daysSinceJoin || 0) - leaveDays;
      if (adjustedDays > maxDays) {
        items.push(makeItem({
          type: 'time_not_tracked',
          title: `No time entries`,
          description: `${user.name || user.email} — never tracked hours`,
          severity: 'warning',
          entity_type: 'user',
          entity_id: user.id,
          entity_label: user.name || user.email,
          user_id: user.id,
          days_overdue: adjustedDays,
          recommended_action: 'Remind team member to log hours',
          navigation_target: 'team-members',
          navigation_params: { userId: user.id },
        }));
      }
      continue;
    }

    const daysSinceTracked = daysBetween(latestDate, todayStr);
    const leaveDays = countLeaveDaysInRange(userLeave, addDaysISO(latestDate, 1), todayStr);
    const adjustedGap = daysSinceTracked - leaveDays;
    if (adjustedGap > maxDays) {
      items.push(makeItem({
        type: 'time_not_tracked',
        title: `Time tracking gap`,
        description: `${user.name || user.email} — ${adjustedGap} days since last entry`,
        severity: adjustedGap > maxDays * 2 ? 'critical' : 'warning',
        entity_type: 'user',
        entity_id: user.id,
        entity_label: user.name || user.email,
        user_id: user.id,
        days_overdue: adjustedGap - maxDays,
        recommended_action: 'Follow up on missing time entries',
        navigation_target: 'team-members',
        navigation_params: { userId: user.id },
      }));
    }
  }
  return items;
}

// TEAM: Hours under target (current month)
function ruleHoursUnderTarget(users, userCurrentMonthHours, config, weeklyAssignments, timeOff) {
  const items = [];
  const now = new Date();
  const currentMonth = now.toISOString().slice(0, 7);
  const dayOfMonth = now.getDate();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const targetPercent = (config.targetPercent || 95) / 100;

  // Only alert if we're past the first week of the month
  if (dayOfMonth < 8) return items;

  // Without the DB-sourced hours map (or if it's for a stale month) we can't
  // trust the in-memory timeEntries slice — emit nothing rather than false 0h alerts.
  if (!userCurrentMonthHours || userCurrentMonthHours._month !== currentMonth) return items;

  // Only evaluate employees active during the period from 2026-04-01 onwards
  // (excludes employees whose employment ended before that date)
  const PERIOD_START = '2026-04-01';
  const activeUsers = users.filter(u =>
    u.isActive !== false &&
    u.status !== 'inactive' &&
    u.role !== 'admin' &&
    (!u.endDate || u.endDate >= PERIOD_START)
  );

  const leaveRangesByUser = buildUserLeaveRanges(timeOff);
  const monthStart = `${currentMonth}-01`;
  const todayStr = now.toISOString().slice(0, 10);

  for (const user of activeUsers) {
    // Floating / unassigned team-board users are general resources and aren't
    // expected to log hours — mirror the ruleTimeNotTracked behaviour.
    if (isUnassignedOrFloating(user, weeklyAssignments)) continue;

    const contractedHours = user.contractedHours || 176;
    // Discount approved/pending absence days from the prorated expectation so a
    // user on vacation/sick leave isn't unfairly flagged.
    const userLeave = leaveRangesByUser.get(String(user.id)) || [];
    const leaveDaysSoFar = countLeaveDaysInRange(userLeave, monthStart, todayStr);
    const effectiveDaysSoFar = Math.max(0, dayOfMonth - leaveDaysSoFar);
    const adjustedProgress = effectiveDaysSoFar / daysInMonth;
    const expectedHoursSoFar = contractedHours * adjustedProgress * targetPercent;

    const actualHours = userCurrentMonthHours[user.id] || userCurrentMonthHours[String(user.id)] || 0;

    if (expectedHoursSoFar <= 0) continue; // entire month-so-far was leave
    if (actualHours < expectedHoursSoFar * 0.75) { // significant shortfall
      const expectedRounded = Math.round(contractedHours * adjustedProgress);
      const percentTracked = expectedRounded > 0 ? Math.round((actualHours / expectedRounded) * 100) : 0;
      items.push(makeItem({
        type: 'hours_under_target',
        title: `Hours below target`,
        description: `${user.name || user.email} — ${Math.round(actualHours)}h of ${expectedRounded}h expected (${percentTracked}%)`,
        severity: percentTracked < 50 ? 'critical' : 'warning',
        entity_type: 'user',
        entity_id: user.id,
        entity_label: user.name || user.email,
        user_id: user.id,
        recommended_action: 'Review time tracking with team member',
        navigation_target: 'team-members',
        navigation_params: { userId: user.id },
      }));
    }
  }
  return items;
}

// TEAM: Overwork warning
function ruleOverworkWarning(users, userCurrentMonthHours, config, weeklyAssignments) {
  const items = [];
  const now = new Date();
  const currentMonth = now.toISOString().slice(0, 7);
  const maxMonthlyPercent = (config.maxMonthlyOverPercent || 120) / 100;

  // Same defensive guard as ruleHoursUnderTarget — without a fresh DB map we
  // can't compute real monthly hours, so skip rather than emit garbage.
  if (!userCurrentMonthHours || userCurrentMonthHours._month !== currentMonth) return items;

  const activeUsers = users.filter(u => u.status === 'active');

  for (const user of activeUsers) {
    if (isUnassignedOrFloating(user, weeklyAssignments)) continue;

    const contractedHours = user.contractedHours || 176;

    const actualHours = userCurrentMonthHours[user.id] || userCurrentMonthHours[String(user.id)] || 0;

    if (actualHours > contractedHours * maxMonthlyPercent) {
      const overPercent = Math.round((actualHours / contractedHours) * 100);
      items.push(makeItem({
        type: 'overwork_warning',
        title: `Overwork detected`,
        description: `${user.name || user.email} — ${Math.round(actualHours)}h logged (${overPercent}% of contracted)`,
        severity: overPercent > 140 ? 'critical' : 'warning',
        entity_type: 'user',
        entity_id: user.id,
        entity_label: user.name || user.email,
        user_id: user.id,
        recommended_action: 'Review workload and consider redistribution',
        navigation_target: 'team-members',
        navigation_params: { userId: user.id },
      }));
    }
  }
  return items;
}

// SYSTEM: User setup incomplete
function ruleUserSetupIncomplete(users) {
  const items = [];

  for (const user of users) {
    if (user.status === 'inactive') continue;
    const issues = [];
    if (!user.authUserId) issues.push('not linked to auth account');
    if (!user.name && !user.email) issues.push('missing name and email');
    if (!user.role) issues.push('no role assigned');
    if (!user.joinDate) issues.push('no join date');
    if (user.status === 'invited') issues.push('invitation pending');

    if (issues.length > 0) {
      items.push(makeItem({
        type: 'user_setup_incomplete',
        title: `User setup incomplete`,
        description: `${user.name || user.email || 'Unknown user'} — ${issues.join(', ')}`,
        severity: issues.includes('not linked to auth account') ? 'critical' : 'warning',
        entity_type: 'user',
        entity_id: user.id,
        entity_label: user.name || user.email,
        user_id: user.id,
        is_silent_failure: true,
        recommended_action: 'Complete user profile setup',
        navigation_target: 'team-members',
        navigation_params: { userId: user.id },
      }));
    }
  }
  return items;
}

// SYSTEM: Data completeness — projects
function ruleDataIncompleteProject(projects) {
  const items = [];
  for (const p of projects) {
    if (p.status === 'completed' || p.status === 'archived') continue;
    const missing = [];
    if (!p.clientId) missing.push('no client');
    if (!p.budget || p.budget <= 0) missing.push('no budget/fee');
    if (!p.projectCode) missing.push('no project code');
    if (!p.stageTimeline || Object.keys(p.stageTimeline || {}).length === 0) missing.push('no timeline');
    if (!p.supervisors || p.supervisors.length === 0) missing.push('no supervisors');

    if (missing.length >= 2) { // Only flag if multiple fields missing
      items.push(makeItem({
        type: 'data_incomplete_project',
        title: `Project data incomplete`,
        description: `${p.name} — ${missing.join(', ')}`,
        severity: 'warning',
        entity_type: 'project',
        entity_id: p.id,
        entity_label: p.name,
        project_id: p.id,
        client_id: p.clientId,
        recommended_action: 'Complete project information',
        navigation_target: 'manage-projects',
        navigation_params: { projectId: p.id },
      }));
    }
  }
  return items;
}

// SYSTEM: Data completeness — clients
function ruleDataIncompleteClient(clients) {
  const items = [];
  for (const c of clients) {
    const missing = [];
    if (!c.organisation && !c.name) missing.push('no name');
    if (!c.email) missing.push('no email');
    if (!c.paymentTermsDays) missing.push('no payment terms');

    if (missing.length >= 1) {
      items.push(makeItem({
        type: 'data_incomplete_client',
        title: `Client data incomplete`,
        description: `${c.organisation || c.name || 'Unknown client'} — ${missing.join(', ')}`,
        severity: 'warning',
        entity_type: 'client',
        entity_id: c.id,
        entity_label: c.organisation || c.name,
        client_id: c.id,
        recommended_action: 'Complete client profile',
        navigation_target: 'clients',
        navigation_params: { clientId: c.id },
      }));
    }
  }
  return items;
}

// SYSTEM: Data completeness — timeline
function ruleDataIncompleteTimeline(projects) {
  const items = [];
  for (const p of projects) {
    if (p.status === 'completed' || p.status === 'archived') continue;
    if (!p.stageTimeline || Object.keys(p.stageTimeline || {}).length === 0) {
      items.push(makeItem({
        type: 'data_incomplete_timeline',
        title: `Timeline not configured`,
        description: `${p.name} — no stage timeline defined`,
        severity: 'warning',
        entity_type: 'project',
        entity_id: p.id,
        entity_label: p.name,
        project_id: p.id,
        is_silent_failure: true,
        recommended_action: 'Set up project stage timeline',
        navigation_target: 'timeline',
        navigation_params: { projectId: p.id },
      }));
    }
  }
  return items;
}

// SYSTEM: Team member data completeness
function ruleDataIncompleteTeamMember(users) {
  const items = [];
  for (const u of users) {
    if (u.status === 'inactive') continue;
    const missing = [];
    if (!u.contractedHours) missing.push('no contracted hours');
    if (!u.officeLocation) missing.push('no office assigned');

    if (missing.length >= 1) {
      items.push(makeItem({
        type: 'data_incomplete_team_member',
        title: `Team member data incomplete`,
        description: `${u.name || u.email} — ${missing.join(', ')}`,
        severity: 'warning',
        entity_type: 'user',
        entity_id: u.id,
        entity_label: u.name || u.email,
        user_id: u.id,
        recommended_action: 'Complete team member profile',
        navigation_target: 'team-members',
        navigation_params: { userId: u.id },
      }));
    }
  }
  return items;
}

// FINANCE: Proforma invoice to send (RADAR notification for admin)
// Scans stageTimeline for proformaInvoiceDate entries without a resolved radar status.
// Only flags dates within the last 90 days or in the future to avoid historical noise.
// SYSTEM: Stage dates the invoice ledger does not corroborate.
// Source is public.data_integrity_findings, refreshed nightly by
// check_stage_date_integrity(). Findings that have been accepted or have
// cleared are already excluded by the hook that loads them.
const INTEGRITY_FIELD_LABEL = { paid: 'Payment date', invoice: 'Invoice date', proforma: 'Proforma date' };
const INTEGRITY_VERDICT_TEXT = {
  bleed_suspected:            'matches another project\'s invoice — this is the cross-project leak signature',
  no_invoice:                 'no invoice exists for this stage',
  invoice_date_mismatch:      'no invoice for this stage carries that date',
  invoice_not_confirmed_paid: 'no paid invoice for this stage carries that date',
  proforma_not_in_ledger:     'no proforma invoice carries that date',
};

function ruleStageDateIntegrity(findings, projects) {
  const items = [];
  const projectById = new Map((projects || []).map(p => [String(p.id), p]));

  for (const f of findings || []) {
    const d = f.detail || {};
    const project = projectById.get(String(d.project_id));
    // A finding whose project has since been deleted has nothing to act on.
    if (d.project_id && !project) continue;

    const fieldLabel = INTEGRITY_FIELD_LABEL[d.field] || 'Date';
    const reason = INTEGRITY_VERDICT_TEXT[f.verdict] || 'the invoice ledger does not corroborate it';
    const stage = d.stage_key || '';
    const stageLabel = stage.includes('__part__') ? `${stage.split('__part__')[0]} (part)` : stage;
    const projectLabel = project?.name || d.project_code || 'Unknown project';

    items.push(makeItem({
      type: 'data_integrity_stage_dates',
      // Subject is stable per (stage, field), so acknowledging or dismissing
      // one finding sticks across nightly runs.
      id: `data_integrity_stage_dates-${f.subject}`,
      title: `${fieldLabel} not backed by an invoice`,
      description: `${projectLabel} — ${stageLabel}: ${d.date_value} ${reason}`,
      severity: f.verdict === 'bleed_suspected' ? 'critical' : 'warning',
      domain: 'finance',
      entity_type: 'stage_date_finding',
      entity_id: f.subject,
      entity_label: `${d.project_code || ''} ${stageLabel}`.trim(),
      project_id: project?.id || null,
      detected_at: (f.firstSeenAt || '').slice(0, 10) || today(),
      is_silent_failure: true,
      recommended_action: f.verdict === 'bleed_suspected'
        ? 'Check immediately — a date has been copied from another project'
        : `Check the ${fieldLabel.toLowerCase()} against the invoice and correct whichever is wrong`,
      navigation_target: 'timeline',
      navigation_params: project?.id ? { projectId: project.id } : null,
      meta: { verdict: f.verdict, field: d.field, ledgerInvoices: d.ledger_invoices || null },
    }));
  }
  return items;
}

// PROJECTS: a press-kit folder was rearranged or deleted in Dropbox
// ----------------------------------------------------------------------------
// Source is comms.press_folder_alerts, written by the dropbox-webhook edge
// function when Dropbox reports that something under /BRIO-press moved. The
// Comms page shows the same rows with the buttons that fix them; RADAR's job is
// only to make sure somebody notices, since folder drift is exactly the kind of
// thing nobody thinks to go and look for.
//
// Dismissing or resolving a finding in Comms takes it out of `status = 'open'`,
// so it leaves RADAR on its own — there is one source of truth and no second
// acknowledgement to keep in step.
// Every kind is critical. Somebody changed the press tree from outside the app
// and nothing here agreed to it, which is a different class of problem from work
// that is merely late — there is no due date to be early or late against, and no
// version of it that resolves itself. Grading these against each other only ever
// decided which ones got scrolled past.
//
// `unexpected` is the one to watch: a folder appearing is ordinary housekeeping
// far more often than it is a mistake, and it is now as loud as a deleted
// project folder. If it starts crying wolf, turn off includeUnexpected in RADAR
// settings rather than softening the rest.
const PRESS_DRIFT_META = {
  project_missing: {
    severity: 'critical',
    title: 'Press folder not found in Dropbox',
    action: 'Find the folder in Dropbox — it was renamed beyond recognition, moved, or deleted',
  },
  missing: {
    severity: 'critical',
    title: 'Press folder deleted in Dropbox',
    action: 'Recreate it from the project\u2019s press kit, or drop it from the folder schema',
  },
  // Both ends are known exactly — Dropbox reported the folder leaving one place
  // and arriving in another — so the action can name them instead of asking
  // somebody to go and work out what happened.
  moved: {
    severity: 'critical',
    title: 'Press folder moved in Dropbox',
    action: 'Open the project\u2019s press kit for the exact folder to drag back, and where it belongs',
  },
  renamed: {
    severity: 'critical',
    title: 'Press folder renamed in Dropbox',
    action: 'Rename it back, or re-share any links that pointed at the old name',
  },
  unexpected: {
    severity: 'critical',
    title: 'Unexpected folder in a press kit',
    action: 'Approve it from the project\u2019s press kit if it belongs, or move its contents into a schema folder',
  },
};

// "Edmund Sumner: Photography \u2192 Press Release", using paths relative to the
// project folder so the line reads like Dropbox rather than like a database.
const PRESS_BASE_PREFIX = '/Architecture Brio/BRIO-press/';
function pressDriftRelative(path) {
  if (!path) return '';
  if (!path.startsWith(PRESS_BASE_PREFIX)) return path;
  const rest = path.slice(PRESS_BASE_PREFIX.length);
  const slash = rest.indexOf('/');
  return slash === -1 ? rest : rest.slice(slash + 1);
}
function pressDriftMoveLabel(a) {
  const d = a.detail || {};
  const name = d.name || a.label || 'A folder';
  const from = pressDriftRelative(d.fromParent);
  const to = pressDriftRelative(d.toParent);
  return from && to ? `${name}: ${from} \u2192 ${to}` : name;
}

function rulePressFolderDrift(alerts, projects, config) {
  const items = [];
  const byCode = new Map((projects || []).map(p => [String(p.projectCode || p.project_code), p]));
  const includeUnexpected = config?.includeUnexpected !== false;

  for (const a of alerts || []) {
    const meta = PRESS_DRIFT_META[a.kind];
    // An unknown kind is a newer webhook than this build; skip rather than
    // render an item with no wording and no action.
    if (!meta) continue;
    if (a.kind === 'unexpected' && !includeUnexpected) continue;

    const project = byCode.get(String(a.projectCode));
    const projectLabel = project?.name || a.projectCode || 'Unknown project';
    const detectedOn = (a.detectedAt || '').slice(0, 10) || today();
    const daysOpen = Math.max(0, daysBetween(detectedOn, today()) ?? 0);
    // A move is the one kind where the label alone says nothing useful: the
    // point is which two folders are involved, so both are named here.
    const what = a.kind === 'moved'
      ? pressDriftMoveLabel(a)
      : (a.label || a.path || '');

    items.push(makeItem({
      type: 'press_folder_drift',
      // Keyed on the alert row, which the webhook keeps stable for as long as
      // the finding stays open — so acknowledging one sticks across re-scans.
      id: `press_folder_drift-${a.id}`,
      title: meta.title,
      description: what ? `${projectLabel} — ${what}` : projectLabel,
      severity: meta.severity,
      domain: 'projects',
      entity_type: 'press_folder_alert',
      entity_id: a.id,
      entity_label: `${a.projectCode || ''} ${what}`.trim(),
      project_id: project?.id || null,
      detected_at: detectedOn,
      // Folder drift has no due date, so age is the only honest measure of
      // urgency — and the one Primary Risk ranks on. It is also what lifts these
      // out of the This Week bucket: computeTimeBucket promotes a critical item
      // to Today once it has been open at least a day, and a drift nobody has
      // touched since yesterday is exactly what should lead the page.
      days_overdue: daysOpen,
      // Nothing in the app did this — somebody changed Dropbox directly, which
      // is the definition of a change that would otherwise go unnoticed.
      is_silent_failure: true,
      recommended_action: meta.action,
      // CommsPage keeps its open project in component state rather than in the
      // page route, so this lands on the projects list — where the drift banner
      // names the affected projects and is one click from the press kit.
      navigation_target: 'comms-projects',
      navigation_params: a.projectCode ? { projectCode: a.projectCode } : null,
      meta: { kind: a.kind, path: a.path, detail: a.detail || null },
    }));
  }
  return items;
}

function ruleProformaInvoiceToSend(projects, clients) {
  const items = [];
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 90);
  const cutoffStr = cutoff.toISOString().slice(0, 10);

  for (const project of projects) {
    if (project.status === 'completed' || project.status === 'archived') continue;
    const radarClient = clients.find(c => c.id === project.clientId);
    if (!(radarClient?.requiresProformaInvoice ?? project.requiresProformaInvoice)) continue;
    const timeline = project.stageTimeline;
    if (!timeline || typeof timeline !== 'object') continue;

    const client = clients.find(c => String(c.id) === String(project.clientId));

    for (const [stageKey, stageData] of Object.entries(timeline)) {
      if (!stageData.proformaInvoiceDate) continue;
      // Skip historical dates older than 90 days
      if (stageData.proformaInvoiceDate < cutoffStr) continue;
      // Skip if the proforma status is already marked as handled in the timeline
      const emailStatus = stageData.emailStatus?.proformaInvoiceDate;
      if (emailStatus === 'sent') continue;

      items.push(makeItem({
        type: 'invoice_proforma_to_send',
        title: `Send proforma invoice`,
        description: `${project.name} — ${stageKey} — proforma date: ${stageData.proformaInvoiceDate}`,
        severity: 'critical',
        domain: 'finance',
        entity_type: 'project_stage',
        entity_id: `${project.id}-${stageKey}-proforma`,
        entity_label: project.name,
        project_id: project.id,
        client_id: project.clientId,
        due_date: stageData.proformaInvoiceDate,
        detected_at: stageData.proformaInvoiceDate,
        recommended_action: 'Prepare and send proforma invoice to client',
        navigation_target: 'timeline',
        navigation_params: {
          projectId: project.id,
          openInvoicePipeline: true,
          stageKey,
          stageName: stageKey,
          invoiceDate: stageData.proformaInvoiceDate,
          invoiceDocType: 'proforma',
        },
        meta: {
          stageKey,
          projectCode: project.projectCode || '',
          projectName: project.name,
          clientName: client?.organisation || client?.name || '',
          notificationType: 'invoice_proforma_to_send',
          mailCategory: 'invoice',
          recommendedSenderIdentity: 'admin/accounting',
          recommendedSubject: `${project.projectCode || 'PROJECT'}_Invoice Proforma Invoice`,
          recommendedTemplateKey: 'proforma_invoice_admin',
          relatedField: 'proformaInvoiceDate',
          relatedDate: stageData.proformaInvoiceDate,
        },
      }));
    }
  }
  return items;
}

// FINANCE: Tax invoice to send (RADAR notification for admin)
// Scans stageTimeline for invoiceDate entries that should route to admin.
// Only flags dates within the last 90 days or in the future to avoid historical noise.
function ruleTaxInvoiceToSend(projects, clients) {
  const items = [];
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 90);
  const cutoffStr = cutoff.toISOString().slice(0, 10);

  for (const project of projects) {
    if (project.status === 'completed' || project.status === 'archived') continue;
    const timeline = project.stageTimeline;
    if (!timeline || typeof timeline !== 'object') continue;

    const client = clients.find(c => String(c.id) === String(project.clientId));

    for (const [stageKey, stageData] of Object.entries(timeline)) {
      if (!stageData.invoiceDate) continue;
      // Skip historical dates older than 90 days
      if (stageData.invoiceDate < cutoffStr) continue;
      // Skip if the invoice email status is already marked as sent
      const emailStatus = stageData.emailStatus?.invoiceDate;
      if (emailStatus === 'sent') continue;

      items.push(makeItem({
        type: 'invoice_tax_to_send',
        title: `Send tax invoice`,
        description: `${project.name} — ${stageKey} — invoice date: ${stageData.invoiceDate}`,
        severity: 'critical',
        domain: 'finance',
        entity_type: 'project_stage',
        entity_id: `${project.id}-${stageKey}-tax-invoice`,
        entity_label: project.name,
        project_id: project.id,
        client_id: project.clientId,
        due_date: stageData.invoiceDate,
        detected_at: stageData.invoiceDate,
        recommended_action: 'Prepare and send tax invoice to client',
        navigation_target: 'timeline',
        navigation_params: {
          projectId: project.id,
          openInvoicePipeline: true,
          stageKey,
          stageName: stageKey,
          invoiceDate: stageData.invoiceDate,
          invoiceDocType: 'tax',
        },
        meta: {
          stageKey,
          projectCode: project.projectCode || '',
          projectName: project.name,
          clientName: client?.organisation || client?.name || '',
          notificationType: 'invoice_tax_to_send',
          mailCategory: 'invoice',
          recommendedSenderIdentity: 'admin/accounting',
          recommendedSubject: `${project.projectCode || 'PROJECT'}_Invoice Tax Invoice`,
          recommendedTemplateKey: 'tax_invoice_admin',
          relatedField: 'invoiceDate',
          relatedDate: stageData.invoiceDate,
        },
      }));
    }
  }
  return items;
}

// ── Main Hook ────────────────────────────────────────────────────────────────

export function useRadar({
  projects = [],
  clients = [],
  users = [],
  invoices = [],
  timeEntries = [],
  userLastEntryDates = {},
  userCurrentMonthHours = {},
  inquiries = [],
  candidates = [],
  interviews = [],
  radarSettings = {},
  itemStatuses = [],
  currentUser = null,
  fmtCurr = null,
  weeklyAssignments = null,
  timeOff = [],
  integrityFindings = [],
  pressFolderAlerts = [],
} = {}) {

  const items = useMemo(() => {
    const getConfig = (ruleKey) => {
      const setting = radarSettings[ruleKey];
      return setting?.config || {};
    };
    const isEnabled = (ruleKey) => {
      const setting = radarSettings[ruleKey];
      return setting?.enabled !== false;
    };

    let allItems = [];

    // Run all enabled rules
    if (isEnabled('invoice_overdue'))
      allItems.push(...ruleInvoiceOverdue(invoices, clients, projects, getConfig('invoice_overdue')));
    if (isEnabled('invoice_not_sent'))
      allItems.push(...ruleInvoiceNotSent(invoices, projects, getConfig('invoice_not_sent')));
    if (isEnabled('payment_received'))
      allItems.push(...rulePaymentReceived(invoices));
    if (isEnabled('invoice_reminder'))
      allItems.push(...ruleInvoiceReminder(invoices, clients, projects, getConfig('invoice_reminder')));
    if (isEnabled('ca_monthly_reminder'))
      allItems.push(...ruleCaMonthlyReminder(getConfig('ca_monthly_reminder'), invoices));
    if (isEnabled('timeline_overdue'))
      allItems.push(...ruleTimelineOverdue(projects, getConfig('timeline_overdue')));
    if (isEnabled('profitability_risk'))
      allItems.push(...ruleProfitabilityRisk(projects, invoices, timeEntries, users, fmtCurr));
    if (isEnabled('new_inquiry'))
      allItems.push(...ruleNewInquiry(inquiries, getConfig('new_inquiry')));
    if (isEnabled('inquiry_followup_overdue'))
      allItems.push(...ruleInquiryFollowupOverdue(inquiries, getConfig('inquiry_followup_overdue')));
    if (isEnabled('hiring_stagnation'))
      allItems.push(...ruleHiringStagnation(candidates, interviews, getConfig('hiring_stagnation')));
    // Hiring admin actions always enabled (no config toggle needed)
    allItems.push(...ruleHiringAdminAction(candidates));
    if (isEnabled('client_birthday'))
      allItems.push(...ruleClientBirthday(clients, getConfig('client_birthday')));
    if (isEnabled('client_anniversary'))
      allItems.push(...ruleClientAnniversary(clients, getConfig('client_anniversary')));
    if (isEnabled('time_not_tracked'))
      allItems.push(...ruleTimeNotTracked(users, getConfig('time_not_tracked'), userLastEntryDates, weeklyAssignments, timeOff));
    if (isEnabled('hours_under_target'))
      allItems.push(...ruleHoursUnderTarget(users, userCurrentMonthHours, getConfig('hours_under_target'), weeklyAssignments, timeOff));
    if (isEnabled('overwork_warning'))
      allItems.push(...ruleOverworkWarning(users, userCurrentMonthHours, getConfig('overwork_warning'), weeklyAssignments));
    if (isEnabled('user_setup_incomplete'))
      allItems.push(...ruleUserSetupIncomplete(users));
    if (isEnabled('data_incomplete_project'))
      allItems.push(...ruleDataIncompleteProject(projects));
    if (isEnabled('data_incomplete_client'))
      allItems.push(...ruleDataIncompleteClient(clients));
    if (isEnabled('data_incomplete_timeline'))
      allItems.push(...ruleDataIncompleteTimeline(projects));
    if (isEnabled('data_incomplete_team_member'))
      allItems.push(...ruleDataIncompleteTeamMember(users));
    if (isEnabled('invoice_proforma_to_send'))
      allItems.push(...ruleProformaInvoiceToSend(projects, clients));
    if (isEnabled('invoice_tax_to_send'))
      allItems.push(...ruleTaxInvoiceToSend(projects, clients));
    if (isEnabled('data_integrity_stage_dates'))
      allItems.push(...ruleStageDateIntegrity(integrityFindings, projects));
    if (isEnabled('press_folder_drift'))
      allItems.push(...rulePressFolderDrift(pressFolderAlerts, projects, getConfig('press_folder_drift')));

    // Compute time buckets
    for (const item of allItems) {
      item.time_bucket = computeTimeBucket(item);
    }

    // Merge with persisted statuses (acknowledge, snooze, resolve, dismiss)
    const todayStr = today();
    for (const item of allItems) {
      const persisted = itemStatuses.find(s =>
        s.rule_key === item.type &&
        s.entity_type === item.entity_type &&
        String(s.entity_id) === String(item.entity_id)
      );
      if (persisted) {
        // Dismissed items are permanently hidden
        if (persisted.status === 'dismissed') {
          item.status = 'dismissed';
          item.dismissed_at = persisted.dismissed_at;
        // Check if snooze has expired
        } else if (persisted.status === 'snoozed' && persisted.snoozed_until) {
          if (persisted.snoozed_until.slice(0, 10) < todayStr) {
            // Snooze expired, item is back to open
            item.status = 'open';
          } else {
            item.status = 'snoozed';
            item.snoozed_until = persisted.snoozed_until;
          }
        } else {
          item.status = persisted.status;
        }
        item.acknowledged_at = persisted.acknowledged_at;
        item.resolved_at = persisted.resolved_at;
        if (persisted.assigned_to) item.assigned_to = persisted.assigned_to;
      }
    }

    // Apply role-based scoping for supervisors
    if (currentUser?.role === 'supervisor') {
      const supervisedProjectIds = (projects || [])
        .filter(p => p.supervisors && p.supervisors.map(String).includes(String(currentUser.id)))
        .map(p => String(p.id));

      allItems = allItems.filter(item => {
        // Supervisors see project-scoped items only for their projects
        if (item.project_id && !supervisedProjectIds.includes(String(item.project_id))) return false;
        // Supervisors don't see system/data completeness or finance items unrelated to projects
        if (['system', 'bd'].includes(item.domain) && !item.project_id) return false;
        return true;
      });
    }

    // Filter out snoozed items (they're still tracked but hidden from main view)
    // Note: we keep them in the data so filters can reveal them
    return allItems;
  }, [projects, clients, users, invoices, timeEntries, userLastEntryDates, userCurrentMonthHours, inquiries, candidates, interviews,
      radarSettings, itemStatuses, currentUser, fmtCurr, weeklyAssignments, timeOff, integrityFindings,
      pressFolderAlerts]);

  // Compute summary statistics
  const summary = useMemo(() => {
    // Match the default list rendering (RadarDashboardPage filters out snoozed +
    // dismissed) so the indicator counts can't drift from what the user sees.
    // Resolved items are also excluded — they've been explicitly cleared.
    const activeItems = items.filter(i =>
      i.status !== 'resolved' &&
      i.status !== 'snoozed' &&
      i.status !== 'dismissed'
    );
    const criticalCount = activeItems.filter(i => i.severity === 'critical').length;
    const warningCount = activeItems.filter(i => i.severity === 'warning').length;
    const signalCount = activeItems.filter(i => i.severity === 'signal').length;
    const silentFailureCount = activeItems.filter(i => i.is_silent_failure).length;

    // Practice Health Score (0-100)
    // Softened deductions so the score has headroom for "improving but still
    // has work" — the old -10/-3 hit 0 with just ~10 criticals.
    // Now: -3 per critical, -0.5 per warning, 0 per signal.
    const rawScore = Math.max(0, 100 - (criticalCount * 3) - (warningCount * 0.5));
    const score = Math.round(Math.min(100, rawScore));

    let label = 'Excellent';
    if (score < 40) label = 'Critical';
    else if (score < 60) label = 'Risk';
    else if (score < 75) label = 'Attention Needed';
    else if (score < 90) label = 'Stable';

    // Domain health
    const domains = ['finance', 'projects', 'bd', 'hiring', 'team', 'system'];
    const domainHealth = {};
    for (const domain of domains) {
      const domainItems = activeItems.filter(i => i.domain === domain);
      const hasCritical = domainItems.some(i => i.severity === 'critical');
      const hasWarning = domainItems.some(i => i.severity === 'warning');
      domainHealth[domain] = hasCritical ? 'red' : hasWarning ? 'amber' : 'green';
    }

    // Primary risk: single biggest issue (most overdue critical item)
    const primaryRisk = activeItems
      .filter(i => i.severity === 'critical')
      .sort((a, b) => (b.days_overdue || 0) - (a.days_overdue || 0))[0] || null;

    return {
      score,
      label,
      criticalCount,
      warningCount,
      signalCount,
      silentFailureCount,
      totalActive: activeItems.length,
      domainHealth,
      primaryRisk,
    };
  }, [items]);

  // Group items by time bucket, then by domain. Deduping (so the same alert
  // isn't repeated across buckets) lives in groupItemsByBucket.
  const grouped = useMemo(() => groupItemsByBucket(items), [items]);

  return {
    items,
    summary,
    grouped,
  };
}
